import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import '@/index.css'
import '@/lib/i18n/config'
import App from '@/App'
import { SplashScreen } from '@/components/common/SplashScreen'
import { initializeMsal } from '@/lib/auth/msal'
import { applyConsent, initConsentMode } from '@/lib/googleAnalytics'
import { reportError } from '@/lib/logger'
import { useCookieConsentStore } from '@/stores/cookie-consent.store'

globalThis.addEventListener('unhandledrejection', (event) => {
  reportError(event.reason, { source: 'unhandledrejection' })
})

type UiStoreState = {
  state?: {
    theme?: string
  }
}

function initializeThemeSync() {
  try {
    const storedData = localStorage.getItem('corvinus-ui')
    let theme = 'light'

    if (storedData) {
      const parsedData = JSON.parse(storedData) as UiStoreState

      if (parsedData.state?.theme) {
        theme = parsedData.state.theme
      }
    }

    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  } catch {
    document.documentElement.classList.remove('dark')
  }
}

function initializeConsent(): void {
  initConsentMode()
  applyConsent(useCookieConsentStore.getState())
  useCookieConsentStore.subscribe(applyConsent)
}

async function bootstrap(): Promise<void> {
  initializeThemeSync()
  initializeConsent()

  const container = document.getElementById('root')
  if (!container) throw new Error('Root element #root not found in index.html')

  const root = createRoot(container)

  // Paint the splash immediately, then swap it for the app. Rendering before
  // MSAL has consumed the redirect response would flash the signed-out state.
  root.render(<SplashScreen />)

  await initializeMsal()

  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}

void bootstrap()
